import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AssetImage } from "@/components/site/AssetImage";
import { useSite } from "@/context/SiteContext";

const NAV = [
  ["Home", "/"],
  ["Toy Drive", "/toy-drive"],
  ["Donate", "/donate"],
  ["Shirts for Olivia", "/shirts-for-olivia"],
  ["About", "/about"],
  ["Get Involved", "/get-involved"],
  ["Contact", "/contact"],
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

const Logo = () => (
  <div className="relative">
    <Link to="/" className="flex min-h-[44px] items-center gap-3 rounded-xl" aria-label="Olivia's Foundation" data-testid="header-logo-link">
      <AssetImage asset="LOGO" size="xs" eager decorative noEdit sizes="144px" className="h-12 w-[72px] rounded-xl" testId="header-logo" />
      <span className="hidden whitespace-nowrap font-display text-lg font-semibold leading-tight sm:block">Olivia's Foundation</span>
    </Link>
  </div>
);

const DonateButton = () => (
  <Link to="/donate" className="of-btn-donate min-h-[44px] px-5" data-testid="header-donate-button">Donate</Link>
);

export function Header({ stripped = false }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { isAdmin } = useSite();
  useEffect(() => setOpen(false), [pathname]);

  const items = isAdmin ? [...NAV, ["Inbox", "/admin/inbox"], ["Admin settings", "/admin/settings"]] : NAV;
  const linkCls = ({ isActive }) =>
    `inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full px-3 text-[15px] font-medium transition-colors ${
      isActive ? "text-[var(--pg-ink)]" : "text-[#1F2328] hover:text-[var(--pg-ink)]"}`;

  return (
    <header className="border-b border-[#E5DDD3] bg-white/95 backdrop-blur-md" data-testid={stripped ? "header-stripped" : "site-header"}>
      <div className="of-container flex h-[72px] items-center justify-between gap-3">
        <Logo />
        {!stripped && (
          <nav aria-label="Main" className="hidden xl:block" data-testid="desktop-nav">
            <ul className="flex items-center gap-0.5">
              {NAV.map(([label, to]) => (
                <li key={to}>
                  <NavLink to={to} end className={linkCls} data-testid={`nav-link-${slug(label)}`}>{label}</NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="flex items-center gap-2">
          {!stripped && (
            <button type="button" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-[#24453D] px-4 text-[15px] font-semibold xl:hidden"
              aria-expanded={open} aria-controls="mobile-nav" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-4 w-4" aria-hidden /> : <Menu className="h-4 w-4" aria-hidden />} Menu
            </button>
          )}
          <DonateButton />
        </div>
      </div>
      {!stripped && open && (
        <nav id="mobile-nav" aria-label="Main" className="border-t border-[#E5DDD3] bg-[#FFFDF9] xl:hidden" data-testid="mobile-nav">
          <ul className="of-container grid gap-1 py-3">
            {items.map(([label, to]) => (
              <li key={to}>
                <NavLink to={to} end className={({ isActive }) => `flex min-h-[48px] items-center rounded-2xl px-4 text-lg font-medium ${
                  isActive ? "bg-[var(--pg-soft)] text-[var(--pg-ink)]" : "hover:bg-[#F4EEE6]"}`} data-testid={`mobile-nav-link-${slug(label)}`}>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
